import fs from 'fs'
import path from 'path'
import log from 'loglevel'
import { exec } from '../util/exec'
import { deployFunction } from '../configuration/interface'

const archiveName = 'build.tar.gz'

// Default upload: pack configured files and folders into one gzipped archive,
// upload it in a single transfer and unpack it on the server.
// Requires the tar binary on both the runner image and the target server.
export const deployUpload: deployFunction = async (_deployConfig, environmentConfig, _serverConfig, dynamicArgs, ssh) => {
  log.info('running deployUpload')
  const { uploadConfig } = environmentConfig
  const { files, folders } = uploadConfig
  const { buildPath } = dynamicArgs

  const entries = [...files, ...folders]
  if (!entries.length) {
    throw new Error('Nothing to upload: uploadConfig.files and uploadConfig.folders are both empty')
  }

  const archivePath = path.join(process.cwd(), archiveName)
  const quotedEntries = entries.map(entry => `'${entry}'`).join(' ')

  try {
    // Compress locally on the runner
    await exec(`tar -czf '${archivePath}' -C '${process.cwd()}' ${quotedEntries}`, { shell: true })
    log.info('Compressed files and folders into', archivePath)

    // Upload the archive in a single transfer
    await ssh.putFile(archivePath, `${buildPath}/${archiveName}`)
    log.info('Uploaded archive to server:', archiveName)

    // Unpack on the server and remove the remote archive
    const unpackCommand = `tar -xzf ${buildPath}/${archiveName} -C ${buildPath} && rm ${buildPath}/${archiveName}`
    const unpackResult = await ssh.execCommand(unpackCommand)
    log.debug('Executed command over ssh:', unpackCommand, unpackResult)
    if (unpackResult.code && unpackResult.code !== 0) {
      log.error(unpackResult.stderr)
      throw new Error('ERROR UNPACKING ARCHIVE ON SERVER')
    }
    if (unpackResult.stderr) {
      log.warn(unpackResult.stderr)
    }
    log.info('Unpacked and removed archive on the server')
  } finally {
    // Always clean up the local archive, even when compression or upload fails
    if (fs.existsSync(archivePath)) {
      fs.unlinkSync(archivePath)
      log.debug('Deleted local archive:', archivePath)
    }
  }

  log.info('finished deployUpload')
}

// Per-file upload kept as an alternative strategy for projects that can't use
// tar (set `deployUpload: deployUploadFiles` in deploy.config.js).
export const deployUploadFiles: deployFunction = async (_deployConfig, environmentConfig, _serverConfig, dynamicArgs, ssh) => {
  log.info('running deployUpload')
  const { uploadConfig } = environmentConfig
  const { files, folders } = uploadConfig
  const { buildPath } = dynamicArgs

  // Upload files one at a time
  // Using for cycle to simplify dealing with async behavior
  for (let i = 0; i < files.length; i++) {
    const file = files[i]
    log.info('Moving ', file)
    const status = await ssh.putFile(file, `${buildPath}/${file}`)
    log.info('status', status, 'file', file)
  }

  // Upload folders one at a time
  // Using for cycle to simplify dealing with async behavior
  for (let i = 0; i < folders.length; i++) {
    const folder = folders[i]
    const status = await ssh.putDirectory(folder, `${buildPath}/${folder}`, {
      recursive: true,
      concurrency: 1,
      tick: function (localPath: string, _remotePath: string, error: Error | null) {
        log.info('Moving ', localPath)
        if (error) {
          log.error(error)
          throw error
        }
      },
    })
    log.info('status', status, 'folder', folder)
  }
  log.info('finished deployUpload')
}
